import React, { useState } from 'react';

import { Caption, Heading } from '@depop/web-thrift-ui/Typography';
import { ArrowTopIcon } from '@depop/web-ui-kit/Icons/ArrowTopIcon';
import { HelpIcon } from '@depop/web-ui-kit/Icons/HelpIcon';
import { Tooltip } from '@depop/web-ui-kit/Tooltip';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { HELP_ARTICLES } from '@/constants/helpUrls.ts';
import {
  getProductsSubtotal,
  calculateShowTaxSummary,
} from '@/modules/checkout/helpers';
import {
  type CheckoutProduct,
  type OrderSummaryData,
} from '@/modules/checkout/types';
import { getCurrencySymbolFromCurrencyCode } from '@/modules/currencies/helpers';
import { ON_IDENTIFIER } from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { SupportedLocation } from '@/modules/location/constants';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';

type Props = {
  checkoutSummary: OrderSummaryData;
  products: CheckoutProduct[];
  canShipItem: boolean;
  isInternationalShippingUnavailable: boolean;
  totalLabel: string;
  hideShippingRow?: boolean;
};

export function PurchaseBreakdownRows({
  checkoutSummary,
  products,
  canShipItem,
  isInternationalShippingUnavailable,
  totalLabel,
  hideShippingRow = false,
}: Props) {
  const [isTaxRowExpanded, setIsTaxRowExpanded] = useState(false);

  const t = useTranslations('checkout');
  const { existing_address_validation_buyer_web } = useExperiments([
    'existing_address_validation_buyer_web',
  ]);

  const isAuShippingPhase3Variant =
    existing_address_validation_buyer_web === ON_IDENTIFIER;

  const { location } = useCurrentLocation();
  const marketplaceFeeExplainerKey =
    location === SupportedLocation.AU
      ? 'OrderSummaryMarketplaceFeeExplainerAu'
      : 'OrderSummaryMarketplaceFeeExplainer';

  const shouldShowTaxSummary = calculateShowTaxSummary(checkoutSummary);
  const isFreeShipping = canShipItem && !Number(checkoutSummary.shipping_price);
  const isDiscountedShipping =
    Boolean(checkoutSummary.original_shipping_price) &&
    checkoutSummary.original_shipping_price !== checkoutSummary.shipping_price;
  const taxLangKey =
    checkoutSummary?.tax_summary?.taxes?.[0]?.name?.toUpperCase();

  const currencySymbol = getCurrencySymbolFromCurrencyCode(
    products[0].price.currencyName
  );

  const productsSubtotal =
    Number(checkoutSummary.products_price) > 0
      ? checkoutSummary.products_price
      : getProductsSubtotal(products);

  const subtotal = `${currencySymbol}${
    canShipItem ? checkoutSummary.products_price : productsSubtotal
  }`;

  const totalPrice = canShipItem
    ? checkoutSummary.total_price
    : getProductsSubtotal(products);

  function getShippingPriceContent() {
    if (isFreeShipping) {
      return t('OrderSummaryShippingFree');
    }

    if (isDiscountedShipping) {
      return (
        <>
          <span className={styles.shippingOriginalPrice}>
            {currencySymbol + checkoutSummary.original_shipping_price}
          </span>{' '}
          {currencySymbol + checkoutSummary.shipping_price}
        </>
      );
    }

    return currencySymbol + checkoutSummary.shipping_price;
  }

  function handleTaxTitleClick() {
    setIsTaxRowExpanded((isExpanded) => !isExpanded);
  }

  if (isInternationalShippingUnavailable) {
    return null;
  }

  return (
    <>
      <div className={styles.breakdownWrapper}>
        <div className={styles.row}>
          <Heading variant="sm" className={styles.breakdownText}>
            {t('OrderSummarySubtotal')}
          </Heading>
          <Heading
            variant="sm"
            className={styles.breakdownText}
            // eslint-disable-next-line no-restricted-syntax
            data-testid="orderSummary__productsPrice"
          >
            {subtotal}
          </Heading>
        </div>

        {!hideShippingRow && (
          <div className={styles.row}>
            <Heading variant="sm" className={styles.breakdownText}>
              {t('OrderSummaryShipping')}
            </Heading>
            {canShipItem ? (
              <Heading
                variant="sm"
                className={clsx(styles.breakdownText, {
                  [styles['breakdownText--green']]: isFreeShipping,
                })}
                // eslint-disable-next-line no-restricted-syntax
                data-testid="orderSummary__shippingPrice"
              >
                {getShippingPriceContent()}
              </Heading>
            ) : (
              <Caption
                variant="lg-regular"
                className={styles.breakdownTextSmall}
                // eslint-disable-next-line no-restricted-syntax
                data-testid="orderSummary__shippingPrice"
              >
                {isAuShippingPhase3Variant
                  ? t('OrderSummaryShippingSelectValidAddress')
                  : t('OrderSummaryShippingEnterShippingAddress')}
              </Caption>
            )}
          </div>
        )}

        {checkoutSummary.buyer_fee_amount && (
          <div className={styles.row}>
            <Heading variant="sm" className={styles.breakdownText}>
              {t('OrderSummaryMarketplaceFeeTitle')}

              <Tooltip maximumWidth={265} text={t(marketplaceFeeExplainerKey)}>
                <div className={styles.icon}>
                  <HelpIcon size={24} />
                </div>
              </Tooltip>
            </Heading>
            <Heading
              variant="sm"
              className={styles.breakdownText}
              // eslint-disable-next-line no-restricted-syntax
              data-testid="orderSummary__marketplaceFee"
            >
              {canShipItem
                ? `${currencySymbol}${checkoutSummary.buyer_fee_amount}`
                : '-'}
            </Heading>
          </div>
        )}

        {shouldShowTaxSummary && (
          <div className={styles['row--tax']}>
            <div className={styles.row}>
              <Heading
                variant="sm"
                className={styles.breakdownText}
                onClick={handleTaxTitleClick}
              >
                {t(`TaxTitle${taxLangKey}`)}
                <button
                  className={clsx(styles.expandButton, {
                    [styles.expanded]: isTaxRowExpanded,
                  })}
                >
                  <ArrowTopIcon
                    size={16}
                    aria-label={t('OrderSummaryTaxExplainerToggle')}
                  />
                </button>
              </Heading>
              <Heading
                variant="sm"
                className={styles.breakdownText}
                // eslint-disable-next-line no-restricted-syntax
                data-testid="orderSummary__taxAmount"
              >
                {canShipItem
                  ? currencySymbol +
                    checkoutSummary?.tax_summary?.total_tax_amount
                  : '-'}
              </Heading>
            </div>

            {isTaxRowExpanded && (
              <div className={styles.expandableRow}>
                <Heading
                  variant="sm"
                  className={clsx(
                    styles.breakdownText,
                    styles['breakdownText--grey']
                  )}
                >
                  {t(`TaxExplainer1${taxLangKey}`)}
                </Heading>
                <Heading
                  variant="sm"
                  className={clsx(
                    styles.breakdownText,
                    styles['breakdownText--grey']
                  )}
                >
                  {t('TaxExplainer2')}
                </Heading>
                <a
                  className={styles.taxLearnMoreLink}
                  href={HELP_ARTICLES.SALES_TAX_FIND_OUT_MORE}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <Caption variant="lg-regular">{t('TaxFindOutMore')}</Caption>
                </a>
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles['row--total']}>
        <Heading variant="md" as="p">
          {totalLabel}
        </Heading>
        {/* eslint-disable-next-line no-restricted-syntax */}
        <Heading variant="md" as="p" data-testid="orderSummary__totalPrice">
          {canShipItem ? currencySymbol + totalPrice : '-'}
        </Heading>
      </div>
    </>
  );
}
